/**
 * Makydo product 360° spin — badge + lightbox
 * ----------------------------------------------
 * A persistent "360°" badge sits on the main gallery image (rendered by
 * snippets/product-spin-360-badge.liquid) — it is NOT part of the photo
 * carousel, so it never moves, flickers, or races the gallery's own
 * autoplay/slide-change transitions. Clicking it opens this lightbox,
 * the same interaction shape as the theme's existing photo-zoom lightbox.
 *
 * The lightbox (snippets/product-spin-360-lightbox.liquid) is reparented to
 * <body> on init so a transformed/animated ancestor (e.g. the gallery's AOS
 * fade-in) can never trap its `position: fixed` — see MDN on containing
 * blocks. Frames aren't fetched until the visitor actually opens it.
 *
 * Same preload-as-Image + canvas draw approach as makydo-section-360-viewer.js
 * (drawImage() never touches the network after the initial preload).
 *
 * Frame filename shape: {prefix}{zero-padded frame number}{suffix}
 *   prefix / suffix are both optional — e.g. "watchproduct-0001.webp" or
 *   plain "0001.webp".
 */
(function () {
  'use strict';

  if (window.__makydoProductSpin360Loaded) return;
  window.__makydoProductSpin360Loaded = true;

  function pad(num, size) {
    var s = String(num);
    while (s.length < size) s = '0' + s;
    return s;
  }

  function frameUrlAt(el, index) {
    var base = el.dataset.baseUrl || '';
    var digits = parseInt(el.dataset.framePadding, 10);
    if (isNaN(digits)) digits = 4;
    var prefix = el.dataset.framePrefix || '';
    var suffix = el.dataset.frameSuffix || '.webp';
    var querySuffix = el.dataset.querySuffix || '';
    if (!base) return '';
    return base + prefix + pad(index, digits) + suffix + querySuffix;
  }

  function buildFrameUrls(el) {
    var count = parseInt(el.dataset.frameCount, 10) || 0;
    var urls = [];
    if (!el.dataset.baseUrl || !count) return urls;
    for (var i = 1; i <= count; i++) {
      urls.push(frameUrlAt(el, i));
    }
    return urls;
  }

  // Two representative frames for the badge's live mini-preview — first
  // frame and roughly a quarter-turn in, so the cross-fade reads clearly as
  // "this object rotates" rather than a near-identical flicker.
  function initPreview(el) {
    var count = parseInt(el.dataset.frameCount, 10) || 0;
    if (!el.dataset.baseUrl || !count) return;
    var frames = el.querySelectorAll('[data-spin-360-preview-frame]');
    if (frames.length < 2) return;
    var secondIndex = Math.min(count, Math.max(2, Math.round(count / 4)));
    frames[0].src = frameUrlAt(el, 1);
    frames[1].src = frameUrlAt(el, secondIndex);
  }

  var BODY_LOCK_CLASS = 'makydo-spin-360-lock-scroll';

  function init(lightbox) {
    teardown(lightbox);

    var canvas = lightbox.querySelector('[data-spin-360-canvas]');
    var stage = lightbox.querySelector('.makydo-spin-360-lightbox__stage');
    if (!canvas || !stage) return;

    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    document.body.appendChild(lightbox);

    var cleanupFns = [];
    lightbox.__makydoSpin360Cleanup = function () {
      cleanupFns.forEach(function (fn) { fn(); });
    };

    var loader = lightbox.querySelector('[data-spin-360-loader]');
    var fill = lightbox.querySelector('[data-spin-360-loader-fill]');
    var hint = lightbox.querySelector('[data-spin-360-hint]');
    var urls = buildFrameUrls(lightbox);
    if (!urls.length) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var reverse = lightbox.dataset.reverseDirection === 'true' ? -1 : 1;
    var frameCount = urls.length;
    var currentIndex = 0;
    var hasPainted = false;
    var isOpen = false;
    var framesRequested = false;

    var total = urls.length;
    var loadedCount = 0;
    var images = new Array(total);

    var zoom = 1;

    function readZoom() {
      var raw = getComputedStyle(stage).getPropertyValue('--makydo-spin-360-mobile-zoom');
      var parsed = parseFloat(raw);
      zoom = window.matchMedia && window.matchMedia('(max-width: 767px)').matches && !isNaN(parsed) && parsed > 1 ? parsed : 1;
    }

    function resizeCanvas() {
      var rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var dpr = window.devicePixelRatio || 1;
      var w = Math.max(1, Math.round(rect.width * dpr));
      var h = Math.max(1, Math.round(rect.height * dpr));
      readZoom();
      if (canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
      paint(images[currentIndex]);
    }

    function paint(im) {
      if (!im || !im.naturalWidth || !canvas.width || !canvas.height) return;
      var cw = canvas.width;
      var ch = canvas.height;
      var iw = im.naturalWidth;
      var ih = im.naturalHeight;
      // "contain" fit, then zoomed past it on mobile — the stage's
      // overflow:hidden crops the overhang, since a "contain" fit alone is
      // always bound by whichever dimension the viewport pins (usually
      // width on mobile), making extra --makydo-spin-360-max-height inert.
      var scale = Math.min(cw / iw, ch / ih) * zoom;
      var dw = iw * scale;
      var dh = ih * scale;
      var dx = (cw - dw) / 2;
      var dy = (ch - dh) / 2;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(im, dx, dy, dw, dh);
      if (!hasPainted) {
        hasPainted = true;
        canvas.classList.add('is-loaded');
      }
    }

    function showFrame(index) {
      var i = ((index % frameCount) + frameCount) % frameCount;
      currentIndex = i;
      paint(images[i]);
    }

    function loadFrames() {
      if (framesRequested) return;
      framesRequested = true;
      urls.forEach(function (url, i) {
        var im = new Image();
        im.decoding = 'async';
        im.onload = function () {
          loadedCount++;
          if (fill) fill.style.width = Math.round((loadedCount / total) * 100) + '%';
          if (i === 0) {
            resizeCanvas();
            showFrame(0);
          }
          if (loadedCount >= total) {
            if (loader) loader.classList.add('is-hidden');
            startAuto();
          }
        };
        im.onerror = function () {
          loadedCount++;
          if (fill) fill.style.width = Math.round((loadedCount / total) * 100) + '%';
          if (loadedCount >= total) {
            if (loader) loader.classList.add('is-hidden');
            startAuto();
          }
        };
        im.src = url;
        images[i] = im;
      });
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(resizeCanvas);
      ro.observe(stage);
      cleanupFns.push(function () { ro.disconnect(); });
    } else {
      window.addEventListener('resize', resizeCanvas);
      cleanupFns.push(function () { window.removeEventListener('resize', resizeCanvas); });
    }

    // ---- Auto-rotate ----
    var autoRafId = 0;
    var autoLastTime = 0;
    var autoRunning = false;
    var resumeTimer = 0;
    var speed = parseFloat(lightbox.dataset.rotateSpeed) || 24;
    var resumeDelaySec = parseFloat(lightbox.dataset.rotateResumeDelay);
    var RESUME_DELAY = isNaN(resumeDelaySec) ? 1500 : Math.max(0, resumeDelaySec * 1000);

    function autoStep(time) {
      if (!autoRunning) return;
      if (!autoLastTime) autoLastTime = time;
      var elapsed = time - autoLastTime;
      var msPerFrame = 1000 / speed;
      if (elapsed >= msPerFrame) {
        autoLastTime = time;
        showFrame(currentIndex + reverse);
        if (hint) hint.classList.add('is-hidden');
      }
      autoRafId = requestAnimationFrame(autoStep);
    }

    function startAuto() {
      if (reduceMotion || autoRunning || !isOpen || loadedCount < total) return;
      autoRunning = true;
      autoLastTime = 0;
      autoRafId = requestAnimationFrame(autoStep);
    }

    function stopAuto() {
      autoRunning = false;
      if (autoRafId) cancelAnimationFrame(autoRafId);
      autoRafId = 0;
    }

    // ---- Open / close ----
    function open() {
      if (isOpen) return;
      isOpen = true;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add(BODY_LOCK_CLASS);
      loadFrames();
      resizeCanvas();
      if (loadedCount >= total) startAuto();
      var closeBtn = lightbox.querySelector('[data-spin-360-close].makydo-spin-360-lightbox__close');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove(BODY_LOCK_CLASS);
      stopAuto();
      if (resumeTimer) clearTimeout(resumeTimer);
      // Fresh lookup rather than a captured reference — the badge can be a
      // completely different DOM node by now (Shopify theme-editor section
      // re-renders replace elements outright), so an old reference would
      // silently fail to receive focus.
      var currentTrigger = document.querySelector(
        '[data-spin-360-trigger][data-spin-360-target="' + (lightbox.getAttribute('data-spin-360-id') || '') + '"]'
      );
      if (currentTrigger) currentTrigger.focus();
    }

    // Expose for the document-level delegated click handler below — this is
    // deliberately NOT a direct addEventListener on the trigger element.
    // That element can be swapped out from under us by a theme-editor
    // section re-render after init() already ran, leaving a stale listener
    // on a detached node while the visible (new) badge has none at all —
    // exactly the "cursor shows pointer, click does nothing" symptom.
    // Delegation re-resolves the trigger fresh at click time instead.
    lightbox.__makydoSpin360Open = open;

    var closeTriggers = lightbox.querySelectorAll('[data-spin-360-close]');
    closeTriggers.forEach(function (elClose) { elClose.addEventListener('click', close); });
    cleanupFns.push(function () {
      closeTriggers.forEach(function (elClose) { elClose.removeEventListener('click', close); });
    });

    function onKeydown(e) {
      if (e.key === 'Escape' && isOpen) close();
    }
    document.addEventListener('keydown', onKeydown);
    cleanupFns.push(function () { document.removeEventListener('keydown', onKeydown); });

    cleanupFns.push(function () {
      stopAuto();
      if (resumeTimer) clearTimeout(resumeTimer);
      if (isOpen) document.documentElement.classList.remove(BODY_LOCK_CLASS);
    });

    // ---- Drag / swipe scrubbing ----
    var dragging = false;
    var startX = 0;
    var startIndex = 0;
    var sensitivity = parseFloat(lightbox.dataset.dragSensitivity) || 4;
    var dragRafId = 0;
    var pendingDx = 0;
    var hasPendingDx = false;

    function onPointerDown(e) {
      if (!isOpen) return;
      dragging = true;
      startX = e.clientX;
      startIndex = currentIndex;
      stage.classList.add('is-dragging');
      if (hint) hint.classList.add('is-hidden');
      stopAuto();
      if (stage.setPointerCapture && e.pointerId != null) {
        try { stage.setPointerCapture(e.pointerId); } catch (err) {}
      }
    }

    // pointermove can fire far faster than the display refreshes, so
    // painting straight from the event handler leaves the canvas racing
    // ahead of/behind the compositor and reads as choppy "steps" instead of
    // a smooth drag. Instead we just record the latest offset and let a
    // single rAF per frame apply it, matching paint rate to refresh rate.
    function applyPendingDrag() {
      dragRafId = 0;
      if (!hasPendingDx) return;
      hasPendingDx = false;
      var steps = Math.trunc(pendingDx / sensitivity);
      showFrame(startIndex - steps);
    }

    function onPointerMove(e) {
      if (!dragging) return;
      pendingDx = e.clientX - startX;
      hasPendingDx = true;
      if (!dragRafId) dragRafId = requestAnimationFrame(applyPendingDrag);
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      if (dragRafId) {
        cancelAnimationFrame(dragRafId);
        dragRafId = 0;
      }
      hasPendingDx = false;
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(startAuto, RESUME_DELAY);
    }

    stage.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    window.addEventListener('pointercancel', onPointerUp, { passive: true });
    window.addEventListener('blur', onPointerUp);

    cleanupFns.push(function () {
      stage.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('blur', onPointerUp);
      if (dragRafId) cancelAnimationFrame(dragRafId);
    });

    // ---- Background preload ----
    // loadFrames() is otherwise only triggered by open() — the visitor pays
    // for the full download the moment they tap the badge. Warming it in
    // the background once the page itself is done loading means the frames
    // are already cache-hot by the time anyone clicks, so only the very
    // first page load (not the first click) sees the wait. Skipped on
    // Data Saver connections so we don't burn a visitor's data budget on a
    // viewer they may never open.
    function schedulePreload() {
      if (framesRequested || !lightbox.isConnected) return;
      if (navigator.connection && navigator.connection.saveData) return;
      loadFrames();
    }

    var preloadTimer = 0;
    function armPreload() {
      if ('requestIdleCallback' in window) {
        preloadTimer = requestIdleCallback(schedulePreload, { timeout: 4000 });
      } else {
        preloadTimer = setTimeout(schedulePreload, 2000);
      }
    }

    if (document.readyState === 'complete') {
      armPreload();
    } else {
      window.addEventListener('load', armPreload, { once: true });
    }

    cleanupFns.push(function () {
      window.removeEventListener('load', armPreload);
      if (preloadTimer) {
        if ('cancelIdleCallback' in window) cancelIdleCallback(preloadTimer);
        else clearTimeout(preloadTimer);
      }
    });
  }

  function teardown(lightbox) {
    if (lightbox.__makydoSpin360Cleanup) {
      lightbox.__makydoSpin360Cleanup();
      lightbox.__makydoSpin360Cleanup = null;
    }
  }

  function boot(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-makydo-product-spin-360]').forEach(init);
    scope.querySelectorAll('[data-spin-360-preview]').forEach(initPreview);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Quick view / featured product carousels inject markup after load.
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-makydo-product-spin-360]')) init(node);
          if (node.matches && node.matches('[data-spin-360-preview]')) initPreview(node);
          if (node.querySelectorAll) boot(node);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // Delegated so it survives the trigger badge being replaced by a
  // theme-editor section re-render after the lightbox's own init() ran —
  // see the comment above lightbox.__makydoSpin360Open in init().
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-spin-360-trigger]');
    if (!trigger) return;
    // getAttribute, not .dataset — dataset's camelCase conversion only
    // collapses a dash before a lowercase LETTER, not a digit, so
    // "data-spin-360-target" becomes the property dataset['spin-360Target'],
    // NOT dataset.spin360Target. Dot-notation access here silently reads
    // undefined forever. getAttribute sidesteps that entirely.
    var target = trigger.getAttribute('data-spin-360-target');
    var lightbox = target && document.querySelector('[data-spin-360-id="' + target + '"]');
    if (!lightbox) {
      // Belt-and-suspenders: a product page normally has exactly one spin
      // lightbox, so fall back to it rather than doing nothing.
      var all = document.querySelectorAll('[data-makydo-product-spin-360]');
      if (all.length === 1) lightbox = all[0];
    }
    if (lightbox && typeof lightbox.__makydoSpin360Open === 'function') {
      lightbox.__makydoSpin360Open();
    }
  });

  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector && e.target.querySelector('[data-makydo-product-spin-360]');
    if (el) teardown(el);
  });
})();
